/**
 * Shared utilities for cleaning up authentication-related data
 */
import { logger } from "../../infrastructure/services/LoggerService";
import { clearSession } from "./sessionManager";
/**
 * Clear all Keycloak and authentication related data from storage
 * This function is used during logout and error scenarios to ensure clean state
 */
export function cleanAuthData() {
    logger.debug("Cleaning authentication data...");
    // Clear session tracking data (activity timestamps)
    clearSession();
    // Clear sessionStorage (tokens are stored here)
    sessionStorage.removeItem("kc-token");
    sessionStorage.removeItem("kc-refresh-token");
    sessionStorage.removeItem("auth-redirect-path");
    sessionStorage.removeItem("stay-on-error-page");
    // Note: We keep "last-login-email" for login_hint on reconnection
    // Note: We keep "organizationCache" to remember user's organization across sessions
    // Clear cookies that might contain session information
    const cookiesToClear = [
        "KEYCLOAK_SESSION",
        "KEYCLOAK_SESSION_LEGACY",
        "KC_RESTART",
        "KEYCLOAK_IDENTITY",
        "KEYCLOAK_IDENTITY_LEGACY",
    ];
    cookiesToClear.forEach((cookieName) => {
        try {
            // Clear for current domain and path
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
        }
        catch (error) {
            // Silently handle cookie setting errors - they shouldn't prevent cleanup
            logger.debug(`Failed to clear cookie ${cookieName}:`, error);
        }
    });
}
/**
 * sessionStorage key driving the OP-5119 org-less recovery state machine.
 * Present after a State-A discard so the next forced login adds `prompt=login`
 * and so a second org-less token escalates to State-B (logout + terminal page).
 */
export const RECOVERY_FLAG = "kc-org-recovery-attempted";
/**
 * Clear the OP-5119 recovery flag so the next case-(b) detection starts as
 * State-A. Called on a successful auth-with-org-claim and on explicit logout.
 */
export function clearOrgRecoveryFlag() {
    sessionStorage.removeItem(RECOVERY_FLAG);
    logger.debug("Cleared org recovery flag");
}
/**
 * Whether a Keycloak parsed token (id_token or access_token) carries an
 * `organization` claim. Pure helper — pass `keycloak.idTokenParsed` from the
 * keycloak.ts plugin so this stays unit-testable without mocking keycloak-js.
 *
 * Parameter is `unknown` because `organization` is a Keycloak organization-
 * extension claim that isn't part of upstream `KeycloakTokenParsed` typings.
 */
export function hasOrganizationClaim(tokenParsed) {
    if (!tokenParsed || typeof tokenParsed !== "object") {
        return false;
    }
    return !!tokenParsed.organization;
}
/**
 * Clear only redirect-related session storage
 * Used when we want to prevent redirect loops but keep other auth data
 */
export function clearRedirectData() {
    logger.debug("Clearing redirect data...");
    sessionStorage.removeItem("auth-redirect-path");
    sessionStorage.removeItem("stay-on-error-page");
}
/**
 * Remove the cached organization name from localStorage so that the next
 * Keycloak request goes out with the plain `organization` scope. Called
 * before any forced-login redirect (OP-5119) and on explicit logout.
 */
export function clearOrganizationCache() {
    localStorage.removeItem("organizationCache");
    logger.debug("Cleared organization cache");
}
/**
 * Mark logout time and clear auth data
 * Used during logout process
 */
export function markLogoutAndCleanup() {
    logger.debug("Marking logout time and cleaning up...");
    // Mark logout time for preventing auto-login
    sessionStorage.setItem("force-logout", Date.now().toString());
    // Clean all auth data
    cleanAuthData();
    // On explicit logout, also clear organization cache
    // (This is NOT done during page refresh to maintain organization selection)
    clearOrganizationCache();
    // OP-5119: a user-initiated logout always resets the recovery circuit so
    // the next login starts fresh.
    clearOrgRecoveryFlag();
}
/**
 * Save user email for login_hint on reconnection
 * This allows Keycloak to pre-fill or skip the email entry screen
 */
export function saveLoginEmail(email) {
    if (email && email.trim()) {
        sessionStorage.setItem("last-login-email", email.trim());
        logger.debug(`Saved login email for reconnection: ${email}`);
    }
}
/**
 * Get saved login email for login_hint
 * Returns null if no email is saved
 */
export function getLoginEmail() {
    const email = sessionStorage.getItem("last-login-email");
    if (email) {
        logger.debug(`Retrieved login email for reconnection: ${email}`);
    }
    return email;
}
/**
 * Clear saved login email
 * Used when changing organization or for fresh login
 */
export function clearLoginEmail() {
    sessionStorage.removeItem("last-login-email");
    logger.debug("Cleared saved login email");
}
